import { paymentService, StripeCustomer } from './paymentService';
import { PLAN_CONFIG, calculateProPrice } from '../config/stripeConfig';
import { getProMonthlyEmailLimit } from '../config/proEmailLimits';
import { doc, setDoc, getDoc, Timestamp, collection, query, where, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { planPricingService } from './planPricingService';

/** Stored on `subscription.emails` for Business; enforcement uses unlimited in `checkSubscriptionLimits`. */
export const UNLIMITED_EMAILS_SENTINEL = 1_000_000_000;

/** Re-export for callers that used the old name */
export { getProMonthlyEmailLimit as proMonthlyEmailAllocation } from '../config/proEmailLimits';

function emailsForPaidPlan(plan: string, subscribers: number): number {
  if (plan === 'business') {
    return UNLIMITED_EMAILS_SENTINEL;
  }
  return getProMonthlyEmailLimit(subscribers);
}

// Types for subscription management
export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  subscribers: number;
  emails: number;
  features: string[];
  stripePriceId?: string;
}

export interface UserSubscription {
  id: string;
  userId: string;
  plan: string;
  status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'unpaid';
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  paymentProvider?: 'stripe' | 'razorpay' | 'dodo';
  /** Last checkout payment id (e.g. Razorpay `pay_…`, Stripe `pi_…`) for admin / receipts. */
  lastPaymentReference?: string | null;
  dodoPaymentId?: string | null;
  dodoSubscriptionId?: string | null;
  dodoCheckoutSessionId?: string | null;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelAtPeriodEnd: boolean;
  subscribers: number;
  emails: number;
  currency: string;
  billingPeriod?: 'monthly' | 'annual';
  createdAt: Date;
  updatedAt: Date;
}

export interface SubscriptionLimits {
  maxSubscribers: number;
  maxEmails: number;
  usedEmails: number;
  remainingEmails: number;
  canSendEmail: boolean;
  canAddSubscribers: boolean;
}

class SubscriptionService {
  // Get available subscription plans
  getAvailablePlans(): SubscriptionPlan[] {
    return [
      {
        id: PLAN_CONFIG.starter.id,
        name: PLAN_CONFIG.starter.name,
        price: PLAN_CONFIG.starter.price,
        currency: PLAN_CONFIG.starter.currency,
        interval: PLAN_CONFIG.starter.interval,
        subscribers: PLAN_CONFIG.starter.subscribers,
        emails: PLAN_CONFIG.starter.emails,
        features: PLAN_CONFIG.starter.features,
      },
      {
        id: PLAN_CONFIG.pro.id,
        name: PLAN_CONFIG.pro.name,
        price: PLAN_CONFIG.pro.price,
        currency: PLAN_CONFIG.pro.currency,
        interval: PLAN_CONFIG.pro.interval,
        subscribers: PLAN_CONFIG.pro.subscribers,
        emails: PLAN_CONFIG.pro.emails,
        features: PLAN_CONFIG.pro.features,
      },
    ];
  }

  // Calculate pricing for Pro plan based on subscribers
  async calculateProPricing(subscribers: number, currency: string = 'usd'): Promise<SubscriptionPlan> {
    try {
      // Try to get price from pricing service
      const price = await planPricingService.getPriceForSubscribers('pro', subscribers);
      return {
        id: PLAN_CONFIG.pro.id,
        name: PLAN_CONFIG.pro.name,
        price: price,
        currency: currency.toLowerCase(),
        interval: PLAN_CONFIG.pro.interval,
        subscribers: subscribers,
        emails: getProMonthlyEmailLimit(subscribers),
        features: PLAN_CONFIG.pro.features,
      };
    } catch (error) {
      console.warn('Failed to fetch pricing from service, using fallback:', error);
      // Fallback to default calculation
      const pricing = calculateProPrice(subscribers, currency);
      return {
        id: PLAN_CONFIG.pro.id,
        name: PLAN_CONFIG.pro.name,
        price: pricing.price,
        currency: pricing.currency,
        interval: PLAN_CONFIG.pro.interval,
        subscribers: pricing.subscribers,
        emails: pricing.emails,
        features: PLAN_CONFIG.pro.features,
      };
    }
  }

  // Get user's current subscription
  async getUserSubscription(userId: string): Promise<UserSubscription | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (!userDoc.exists()) {
        return null;
      }

      const userData = userDoc.data();
      let subscription = userData.subscription;

      if (!subscription) {
        const settingsDoc = await getDoc(doc(db, 'user_settings', userId));
        if (settingsDoc.exists()) {
          subscription = settingsDoc.data()?.subscription;
        }
      }

      if (!subscription) {
        return null;
      }

      // Get billing period (default to monthly)
      const billingPeriod = subscription.billingPeriod || 'monthly';

      // Get creation date - use createdAt, currentPeriodStart, or current date as fallback
      let periodStart: Date;
      if (subscription.currentPeriodStart?.toDate) {
        const storedStart = subscription.currentPeriodStart.toDate();
        // Validate the stored start date
        if (isNaN(storedStart.getTime()) || storedStart.getTime() === 0) {
          // Invalid date, use createdAt or current date
          if (subscription.createdAt?.toDate) {
            periodStart = subscription.createdAt.toDate();
          } else {
            periodStart = new Date();
          }
        } else {
          periodStart = storedStart;
        }
      } else if (subscription.createdAt?.toDate) {
        periodStart = subscription.createdAt.toDate();
      } else {
        // If no dates exist, use current date (shouldn't happen for valid subscriptions)
        periodStart = new Date();
      }

      // Calculate period end based on billing period
      let periodEnd: Date;
      let needsDateFix = false;

      if (subscription.currentPeriodEnd?.toDate) {
        const storedEnd = subscription.currentPeriodEnd.toDate();
        // Validate that the stored end date makes sense
        // If it's invalid, same as start, or too close to start (less than 25 days for monthly or less than 300 days for annual), recalculate
        const daysDiff = (storedEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24);
        const expectedDays = billingPeriod === 'annual' ? 365 : 30;
        const minDays = billingPeriod === 'annual' ? 300 : 25;

        if (isNaN(storedEnd.getTime()) ||
          storedEnd.getTime() <= periodStart.getTime() ||
          daysDiff < minDays ||
          Math.abs(daysDiff - expectedDays) > 5) { // Allow 5 days tolerance
          // Recalculate based on billing period
          needsDateFix = true;
          if (billingPeriod === 'annual') {
            periodEnd = new Date(periodStart.getTime() + 365 * 24 * 60 * 60 * 1000);
          } else {
            periodEnd = new Date(periodStart.getTime() + 30 * 24 * 60 * 60 * 1000);
          }
        } else {
          periodEnd = storedEnd;
        }
      } else {
        // Calculate period end from period start and billing period
        needsDateFix = true;
        if (billingPeriod === 'annual') {
          periodEnd = new Date(periodStart.getTime() + 365 * 24 * 60 * 60 * 1000);
        } else {
          periodEnd = new Date(periodStart.getTime() + 30 * 24 * 60 * 60 * 1000);
        }
      }

      // If dates were recalculated, save them back to the database (async, don't wait)
      if (needsDateFix) {
        this.fixSubscriptionDates(userId, periodStart, periodEnd, billingPeriod).catch(err => {
          console.warn('Failed to fix subscription dates:', err);
        });
      }

      // Check if subscription has expired
      const now = new Date();
      let derivedStatus = subscription.status || 'inactive';

      // If it's a paid plan and the period has ended, mark as expired/inactive for logic purposes
      // (Wait, we'll keep the DB status but compute validity)
      const planLc = (subscription.plan || '').toLowerCase();
      const isExpired =
        planLc !== 'starter' && planLc !== 'free' && periodEnd.getTime() < now.getTime();
      if (isExpired && derivedStatus === 'active') {
        // Log expiration for debugging
        console.log(`SubscriptionService: Subscription for ${userId} has expired on ${periodEnd}`);
      }

      return {
        id: subscription.stripeSubscriptionId || 'local',
        userId,
        plan: subscription.plan,
        status: derivedStatus,
        stripeCustomerId: subscription.stripeCustomerId || null,
        stripeSubscriptionId: subscription.stripeSubscriptionId || null,
        paymentProvider: subscription.paymentProvider,
        lastPaymentReference: subscription.lastPaymentReference ?? undefined,
        dodoPaymentId: subscription.dodoPaymentId ?? null,
        dodoSubscriptionId: subscription.dodoSubscriptionId ?? null,
        dodoCheckoutSessionId: subscription.dodoCheckoutSessionId ?? null,
        currentPeriodStart: periodStart,
        currentPeriodEnd: periodEnd,
        cancelAtPeriodEnd: subscription.cancelAtPeriodEnd || false,
        subscribers: subscription.subscribers || 0,
        emails: subscription.emails || 0,
        currency: subscription.currency || 'usd',
        billingPeriod: billingPeriod,
        createdAt: subscription.createdAt?.toDate() || periodStart,
        updatedAt: subscription.updatedAt?.toDate() || new Date(),
      };
    } catch (error) {
      console.error('Error getting user subscription:', error);
      throw new Error('Failed to get user subscription');
    }
  }

  // Fix subscription dates in Firestore (helper method)
  private async fixSubscriptionDates(
    userId: string,
    periodStart: Date,
    periodEnd: Date,
    billingPeriod: 'monthly' | 'annual'
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const subscription = userData.subscription || {};

        // Update subscription with corrected dates
        await setDoc(userRef, {
          subscription: {
            ...subscription,
            currentPeriodStart: Timestamp.fromDate(periodStart),
            currentPeriodEnd: Timestamp.fromDate(periodEnd),
            billingPeriod: billingPeriod,
            createdAt: Timestamp.fromDate(periodStart), // Also update createdAt
          },
          updatedAt: Timestamp.now(),
        }, { merge: true });

        // Also update user_settings
        const userSettingsRef = doc(db, 'user_settings', userId);
        const settingsDoc = await getDoc(userSettingsRef);
        const settingsData = settingsDoc.exists() ? settingsDoc.data() : {};
        const settingsSubscription = settingsData.subscription || {};

        await setDoc(userSettingsRef, {
          userId,
          subscription: {
            ...settingsSubscription,
            currentPeriodStart: Timestamp.fromDate(periodStart),
            currentPeriodEnd: Timestamp.fromDate(periodEnd),
            billingPeriod: billingPeriod,
            createdAt: Timestamp.fromDate(periodStart), // Also update createdAt
          },
          updatedAt: Timestamp.now(),
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error fixing subscription dates:', error);
      // Don't throw - this is a background fix
    }
  }

  // Public method to reset subscription dates to today
  async resetSubscriptionDatesToToday(userId: string): Promise<void> {
    try {
      const subscription = await this.getUserSubscription(userId);

      if (!subscription) {
        throw new Error('No subscription found');
      }

      const billingPeriod = subscription.billingPeriod || 'monthly';
      const periodStart = new Date();
      const periodEnd = new Date();

      if (billingPeriod === 'annual') {
        periodEnd.setFullYear(periodEnd.getFullYear() + 1);
      } else {
        periodEnd.setMonth(periodEnd.getMonth() + 1);
      }

      console.log('Resetting subscription dates to:', { periodStart, periodEnd, billingPeriod });

      await this.fixSubscriptionDates(userId, periodStart, periodEnd, billingPeriod);

      console.log('Subscription dates reset successfully');
    } catch (error) {
      console.error('Error resetting subscription dates:', error);
      throw new Error('Failed to reset subscription dates');
    }
  }

  /**
   * Firestore `setDoc(..., { merge: true })` replaces the whole `subscription` map when you pass a new object.
   * Shallow-merge `patch` onto `existing` so Dodo / Stripe metadata is not wiped on partial client updates.
   */
  private mergeSubscriptionFirestoreFields(
    existing: Record<string, unknown> | null | undefined,
    subscriptionData: Partial<UserSubscription>
  ): Record<string, unknown> {
    const merged: Record<string, unknown> =
      existing && typeof existing === 'object' ? { ...existing } : {};
    const d = subscriptionData;

    if (d.plan !== undefined) merged.plan = d.plan;
    if (d.status !== undefined) merged.status = d.status;
    if (d.stripeCustomerId !== undefined) merged.stripeCustomerId = d.stripeCustomerId ?? null;
    if (d.stripeSubscriptionId !== undefined) merged.stripeSubscriptionId = d.stripeSubscriptionId ?? null;
    if (d.currentPeriodStart !== undefined) {
      merged.currentPeriodStart = Timestamp.fromDate(d.currentPeriodStart);
    }
    if (d.currentPeriodEnd !== undefined) {
      merged.currentPeriodEnd = Timestamp.fromDate(d.currentPeriodEnd);
    }
    if (d.cancelAtPeriodEnd !== undefined) merged.cancelAtPeriodEnd = d.cancelAtPeriodEnd;
    if (d.subscribers !== undefined) merged.subscribers = d.subscribers;
    if (d.emails !== undefined) merged.emails = d.emails;
    if (d.currency !== undefined) merged.currency = d.currency;
    if (d.billingPeriod !== undefined) merged.billingPeriod = d.billingPeriod;
    if (d.paymentProvider !== undefined) merged.paymentProvider = d.paymentProvider;
    if (d.lastPaymentReference !== undefined) {
      merged.lastPaymentReference = d.lastPaymentReference ?? null;
    }
    if (d.dodoPaymentId !== undefined) merged.dodoPaymentId = d.dodoPaymentId ?? null;
    if (d.dodoCheckoutSessionId !== undefined) merged.dodoCheckoutSessionId = d.dodoCheckoutSessionId ?? null;
    if (d.dodoSubscriptionId !== undefined) merged.dodoSubscriptionId = d.dodoSubscriptionId ?? null;

    merged.updatedAt = Timestamp.now();
    return merged;
  }

  /**
   * Ensure every account has an active Starter plan (idempotent — never overwrites existing plans).
   */
  async ensureStarterSubscription(userId: string): Promise<UserSubscription> {
    const existing = await this.getUserSubscription(userId);
    if (existing) {
      return existing;
    }

    const purchaseDate = new Date();
    const subscription: UserSubscription = {
      id: 'starter',
      userId,
      plan: 'starter',
      status: 'active',
      stripeCustomerId: null,
      currentPeriodStart: purchaseDate,
      currentPeriodEnd: new Date(purchaseDate.getTime() + 365 * 24 * 60 * 60 * 1000),
      cancelAtPeriodEnd: false,
      subscribers: PLAN_CONFIG.starter.subscribers,
      emails: PLAN_CONFIG.starter.emails,
      currency: PLAN_CONFIG.starter.currency,
      billingPeriod: 'annual',
      createdAt: purchaseDate,
      updatedAt: purchaseDate,
    };

    const userRef = doc(db, 'users', userId);
    await setDoc(
      userRef,
      {
        subscription: this.mergeSubscriptionFirestoreFields(null, subscription),
        updatedAt: Timestamp.now(),
      },
      { merge: true }
    );

    return subscription;
  }

  // Create or update user subscription in Firestore
  async updateUserSubscription(
    userId: string,
    subscriptionData: Partial<UserSubscription>
  ): Promise<void> {
    console.log('SubscriptionService: updateUserSubscription called with', { userId, subscriptionData });
    try {
      const userRef = doc(db, 'users', userId);
      const userSettingsRef = doc(db, 'user_settings', userId);

      console.log('SubscriptionService: User ID:', userId);
      console.log('SubscriptionService: User ref path:', userRef.path);
      console.log('SubscriptionService: User settings ref path:', userSettingsRef.path);

      const userDoc = await getDoc(userRef);
      const settingsDoc = await getDoc(userSettingsRef);

      console.log('SubscriptionService: User document exists:', userDoc.exists());
      if (userDoc.exists()) {
        console.log('SubscriptionService: User document data:', userDoc.data());
      }

      const existingUserSub = userDoc.exists() ? (userDoc.data()?.subscription as Record<string, unknown> | undefined) : undefined;
      const existingSettingsSub = settingsDoc.exists()
        ? (settingsDoc.data()?.subscription as Record<string, unknown> | undefined)
        : undefined;

      const subscriptionForUser = this.mergeSubscriptionFirestoreFields(existingUserSub, subscriptionData);
      const subscriptionForSettings = this.mergeSubscriptionFirestoreFields(existingSettingsSub, subscriptionData);

      console.log('SubscriptionService: Updating user profile with planSelected: true');
      console.log('SubscriptionService: User ref:', userRef.path);
      console.log('SubscriptionService: Subscription data:', subscriptionForUser);
      try {
        await setDoc(
          userRef,
          {
            subscription: subscriptionForUser,
            planSelected: true,
            updatedAt: Timestamp.now()
          },
          { merge: true }
        );
        console.log('SubscriptionService: User profile updated successfully');
      } catch (userError) {
        console.error('SubscriptionService: Error updating user profile:', userError);
        throw userError;
      }

      console.log('SubscriptionService: Updating user settings');
      console.log('SubscriptionService: User settings ref:', userSettingsRef.path);
      try {
        await setDoc(
          userSettingsRef,
          {
            userId,
            subscription: subscriptionForSettings,
            updatedAt: Timestamp.now()
          },
          { merge: true }
        );
        console.log('SubscriptionService: User settings updated successfully');
      } catch (settingsError) {
        console.error('SubscriptionService: Error updating user settings:', settingsError);
        throw settingsError;
      }
    } catch (error) {
      console.error('SubscriptionService: Error updating user subscription:', error);
      console.error('SubscriptionService: Error details:', error);
      console.error('SubscriptionService: Error code:', error instanceof Error ? error.message : 'Unknown error');
      throw new Error(`Failed to update user subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Create a new subscription
  async createSubscription(
    userId: string,
    plan: string,
    subscribers: number,
    currency: string = 'usd',
    billingPeriod: 'monthly' | 'annual' = 'monthly',
    paymentMeta?: { paymentProvider?: 'stripe' | 'razorpay' | 'dodo'; lastPaymentReference?: string }
  ): Promise<UserSubscription> {
    console.log('SubscriptionService: createSubscription called with', { userId, plan, subscribers, currency });
    try {
      // Get user data
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      console.log('SubscriptionService: User data retrieved:', userData);

      // For free plan, skip Stripe customer creation
      let customer: StripeCustomer | null = null;
      if (plan !== 'starter') {
        // Get or create Stripe customer for paid plans only
        if (userData?.subscription?.stripeCustomerId) {
          console.log('SubscriptionService: Using existing Stripe customer');
          customer = await paymentService.getCustomer(userData.subscription.stripeCustomerId);
        } else {
          console.log('SubscriptionService: Creating new Stripe customer');
          customer = await paymentService.createCustomer(
            userData?.email || '',
            userData?.displayName || userData?.firstName + ' ' + userData?.lastName,
            userId
          );
          console.log('SubscriptionService: Stripe customer created:', customer);
        }
      } else {
        console.log('SubscriptionService: Skipping Stripe customer creation for free plan');
      }

      // Calculate pricing
      let pricing;
      if (plan === 'starter') {
        pricing = {
          price: PLAN_CONFIG.starter.price,
          currency,
          subscribers: PLAN_CONFIG.starter.subscribers,
          emails: PLAN_CONFIG.starter.emails,
        };
      } else {
        // Use pricing service
        const price = await planPricingService.getPriceForSubscribers(plan as 'pro' | 'business', subscribers);
        pricing = {
          price: price,
          currency: currency.toLowerCase(),
          subscribers: subscribers,
          emails: emailsForPaidPlan(plan, subscribers),
        };
      }

      // Calculate billing period dates
      const purchaseDate = new Date();
      let periodEndDate: Date;

      if (billingPeriod === 'annual') {
        // For annual: add 365 days (1 year) from purchase date
        periodEndDate = new Date(purchaseDate.getTime() + 365 * 24 * 60 * 60 * 1000);
      } else {
        // For monthly: add 30 days from purchase date
        periodEndDate = new Date(purchaseDate.getTime() + 30 * 24 * 60 * 60 * 1000);
      }

      // For free plan, just update Firestore
      if (plan === 'starter') {
        console.log('SubscriptionService: Creating starter subscription');
        const subscription: UserSubscription = {
          id: 'starter',
          userId,
          plan: 'starter',
          status: 'active',
          stripeCustomerId: customer?.id || null,
          currentPeriodStart: purchaseDate,
          currentPeriodEnd: new Date(purchaseDate.getTime() + 365 * 24 * 60 * 60 * 1000), // 1 year for free plan
          cancelAtPeriodEnd: false,
          subscribers: pricing.subscribers,
          emails: pricing.emails,
          currency: pricing.currency,
          billingPeriod: 'annual', // Free plan is effectively annual
          createdAt: purchaseDate,
          updatedAt: purchaseDate,
        };
        console.log('SubscriptionService: Starter subscription object created:', subscription);

        await this.updateUserSubscription(userId, subscription);
        console.log('SubscriptionService: Starter subscription saved to Firestore');
        return subscription;
      }

      // For paid plans, create Stripe subscription
      // Note: In a real implementation, you would need to create Stripe Price objects
      // and use their price IDs here. For now, we'll simulate this.
      if (!customer) {
        throw new Error('Customer is required for paid plans');
      }

      const subscription: UserSubscription = {
        id: `sub_${Date.now()}`,
        userId,
        plan: plan,
        status: 'active',
        stripeCustomerId: customer.id,
        stripeSubscriptionId: `sub_${Date.now()}`,
        currentPeriodStart: purchaseDate,
        currentPeriodEnd: periodEndDate,
        cancelAtPeriodEnd: false,
        subscribers: pricing.subscribers,
        emails: pricing.emails,
        currency: pricing.currency,
        billingPeriod: billingPeriod,
        createdAt: purchaseDate,
        updatedAt: purchaseDate,
        ...(paymentMeta?.paymentProvider ? { paymentProvider: paymentMeta.paymentProvider } : {}),
        ...(paymentMeta?.lastPaymentReference ? { lastPaymentReference: paymentMeta.lastPaymentReference } : {})
      };

      await this.updateUserSubscription(userId, subscription);
      return subscription;
    } catch (error) {
      console.error('SubscriptionService: Error creating subscription:', error);
      console.error('SubscriptionService: Error details:', error);
      throw new Error(`Failed to create subscription: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  // Update subscription plan
  async updateSubscriptionPlan(
    userId: string,
    newPlan: string,
    newSubscribers: number,
    currency: string = 'usd',
    billingPeriod?: 'monthly' | 'annual',
    resetPeriod: boolean = false, // When true, reset period dates to today (for new payments)
    paymentMeta?: { paymentProvider?: 'stripe' | 'razorpay' | 'dodo'; lastPaymentReference?: string }
  ): Promise<UserSubscription> {
    try {
      const currentSubscription = await this.getUserSubscription(userId);

      if (!currentSubscription) {
        throw new Error('No active subscription found');
      }

      // Calculate new pricing
      let pricing;
      if (newPlan === 'starter') {
        pricing = {
          price: PLAN_CONFIG.starter.price,
          currency,
          subscribers: PLAN_CONFIG.starter.subscribers,
          emails: PLAN_CONFIG.starter.emails,
        };
      } else {
        // Use pricing service
        const price = await planPricingService.getPriceForSubscribers(newPlan as 'pro' | 'business', newSubscribers);
        pricing = {
          price: price,
          currency: currency.toLowerCase(),
          subscribers: newSubscribers,
          emails: emailsForPaidPlan(newPlan, newSubscribers),
        };
      }

      // Determine billing period dates
      let periodStart = currentSubscription.currentPeriodStart;
      let periodEnd = currentSubscription.currentPeriodEnd;
      const effectiveBillingPeriod = billingPeriod || currentSubscription.billingPeriod || 'monthly';

      // Reset period dates if this is a new payment/renewal
      if (resetPeriod) {
        periodStart = new Date();
        periodEnd = new Date();
        if (effectiveBillingPeriod === 'annual') {
          periodEnd.setFullYear(periodEnd.getFullYear() + 1);
        } else {
          periodEnd.setMonth(periodEnd.getMonth() + 1);
        }
        console.log('SubscriptionService: Resetting period dates to:', { periodStart, periodEnd });
      }

      // Update subscription
      const updatedSubscription: Partial<UserSubscription> = {
        plan: newPlan,
        subscribers: pricing.subscribers,
        emails: pricing.emails,
        currency: pricing.currency,
        status: 'active', // Ensure status is active after update
        billingPeriod: effectiveBillingPeriod,
        currentPeriodStart: periodStart,
        currentPeriodEnd: periodEnd,
        createdAt: resetPeriod ? new Date() : currentSubscription.createdAt, // Update createdAt for renewals
        updatedAt: new Date(),
        ...(paymentMeta?.paymentProvider ? { paymentProvider: paymentMeta.paymentProvider } : {}),
        ...(paymentMeta?.lastPaymentReference ? { lastPaymentReference: paymentMeta.lastPaymentReference } : {})
      };

      await this.updateUserSubscription(userId, updatedSubscription);

      return {
        ...currentSubscription,
        ...updatedSubscription,
      } as UserSubscription;
    } catch (error) {
      console.error('Error updating subscription plan:', error);
      throw new Error('Failed to update subscription plan');
    }
  }

  // Cancel subscription
  async cancelSubscription(userId: string, immediately: boolean = false): Promise<void> {
    try {
      const subscription = await this.getUserSubscription(userId);

      if (!subscription || !subscription.stripeSubscriptionId) {
        throw new Error('No active subscription found');
      }

      // Cancel Stripe subscription
      await paymentService.cancelSubscription(subscription.stripeSubscriptionId, immediately);

      // Update local subscription
      const updateData: Partial<UserSubscription> = {
        status: immediately ? 'cancelled' : 'active',
        cancelAtPeriodEnd: !immediately,
        updatedAt: new Date(),
      };

      await this.updateUserSubscription(userId, updateData);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw new Error('Failed to cancel subscription');
    }
  }

  // Get the effective plan ID (e.g., fallback to starter if expired)
  getEffectivePlanId(subscription: UserSubscription | null): string {
    if (!subscription) return 'starter';

    const raw = (subscription.plan || '').toLowerCase();

    // If it's already a starter/free plan, just return it
    if (raw === 'starter' || raw === 'free') {
      return raw === 'free' ? 'free' : 'starter';
    }

    // Check for expiration
    const now = new Date();
    const hasExpired = subscription.currentPeriodEnd.getTime() < now.getTime();

    if (hasExpired) {
      return 'starter'; // Expired paid plan acts like Starter in the UI
    }

    return raw;
  }

  // Check subscription limits
  async checkSubscriptionLimits(userId: string): Promise<SubscriptionLimits> {
    try {
      const subscription = await this.getUserSubscription(userId);

      if (!subscription) {
        return {
          maxSubscribers: 0,
          maxEmails: 0,
          usedEmails: 0,
          remainingEmails: 0,
          canSendEmail: false,
          canAddSubscribers: false,
        };
      }

      // Get current usage (you would implement this based on your usage tracking)
      const usedEmails = await this.getCurrentEmailUsage(userId);

      const planLcLimits = (subscription.plan || '').toLowerCase();
      const isStarterPlan = planLcLimits === 'starter' || planLcLimits === 'free';
      const isUnlimitedEmailPlan = planLcLimits === 'business';
      const proComputed = getProMonthlyEmailLimit(subscription.subscribers || 0);
      const proEmailCap =
        planLcLimits === 'pro'
          ? subscription.emails && subscription.emails < UNLIMITED_EMAILS_SENTINEL
            ? Math.max(proComputed, subscription.emails)
            : proComputed
          : 0;
      const maxEmails = isUnlimitedEmailPlan
        ? Infinity
        : isStarterPlan
          ? PLAN_CONFIG.starter.emails
          : planLcLimits === 'pro'
            ? proEmailCap
            : subscription.emails;
      const maxSubscribers = isStarterPlan ? (PLAN_CONFIG.starter.subscribers) : subscription.subscribers;

      const now = new Date();
      const isExpired =
        !isStarterPlan && subscription.currentPeriodEnd.getTime() < now.getTime();
      const isActive = subscription.status === 'active' && !isExpired;

      return {
        maxSubscribers,
        maxEmails,
        usedEmails,
        remainingEmails: Math.max(0, maxEmails - usedEmails),
        canSendEmail: isActive && (maxEmails - usedEmails) > 0,
        canAddSubscribers: isActive,
      };
    } catch (error) {
      console.error('Error checking subscription limits:', error);
      throw new Error('Failed to check subscription limits');
    }
  }

  // Get current email usage for the month
  private async getCurrentEmailUsage(userId: string): Promise<number> {
    try {
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const periodStartTimestamp = Timestamp.fromDate(periodStart);

      let campaigns: Array<Record<string, unknown>> = [];
      let logs: Array<Record<string, unknown>> = [];

      try {
        const campaignsQuery = query(
          collection(db, 'campaigns'),
          where('userId', '==', userId)
        );
        const campaignsSnapshot = await getDocs(campaignsQuery);
        campaigns = campaignsSnapshot.docs.map((doc) => doc.data());
      } catch (campaignError) {
        console.warn('Error getting email count from campaigns:', campaignError);
      }

      try {
        // Use getCountFromServer to avoid downloading all log documents just
        // to count them. The client-side filter (exclude isSystemEmail + campaignId)
        // maps to server-side where clauses, saving N doc reads per usage check.
        const emailLogsQuery = query(
          collection(db, 'email_logs'),
          where('userId', '==', userId),
          where('status', '==', 'sent'),
          where('sentAt', '>=', periodStartTimestamp),
          where('isSystemEmail', '==', false)
        );
        const countSnap = await getCountFromServer(emailLogsQuery);
        // combineMonthlyEmailUsage also filters out campaignId rows; those go
        // through the campaigns path above so we can use the count directly.
        logs = Array.from({ length: countSnap.data().count }, () => ({} as Record<string, unknown>));
      } catch (emailLogsError) {
        console.warn('Error getting email count from email_logs:', emailLogsError);
      }

      return this.combineMonthlyEmailUsage(campaigns, logs, periodStart);
    } catch (error) {
      console.error('Error getting current email usage:', error);
      return 0;
    }
  }

  private combineMonthlyEmailUsage(
    campaigns: Array<Record<string, unknown>>,
    logs: Array<Record<string, unknown>>,
    periodStart: Date
  ): number {
    let campaignTotal = 0;
    for (const campaign of campaigns) {
      const sentAtRaw = campaign.sentAt as { toDate?: () => Date } | Date | string | undefined;
      const sentAt =
        sentAtRaw && typeof sentAtRaw === 'object' && 'toDate' in sentAtRaw && sentAtRaw.toDate
          ? sentAtRaw.toDate()
          : sentAtRaw
            ? new Date(sentAtRaw as string | Date)
            : null;
      if (sentAt && !Number.isNaN(sentAt.getTime()) && sentAt >= periodStart) {
        const stats = campaign.statistics as { sent?: number } | undefined;
        campaignTotal += stats?.sent || 0;
      }
    }

    const logTotal = logs.filter((log) => {
      if (log.isSystemEmail === true) return false;
      if (log.campaignId) return false;
      return true;
    }).length;

    return campaignTotal + logTotal;
  }

  // Check if user can perform an action
  async canPerformAction(userId: string, action: 'send_email' | 'add_subscriber'): Promise<boolean> {
    try {
      const limits = await this.checkSubscriptionLimits(userId);

      switch (action) {
        case 'send_email':
          return limits.canSendEmail;
        case 'add_subscriber':
          return limits.canAddSubscribers;
        default:
          return false;
      }
    } catch (error) {
      console.error('Error checking action permission:', error);
      return false;
    }
  }

  // Get subscription status for display
  getSubscriptionStatus(subscription: UserSubscription | null): {
    status: string;
    color: string;
    message: string;
  } {
    if (!subscription) {
      return {
        status: 'No Subscription',
        color: 'gray',
        message: 'No active subscription found',
      };
    }

    switch (subscription.status) {
      case 'active': {
        const now = new Date();
        const planLcStatus = (subscription.plan || '').toLowerCase();
        const hasExpired =
          planLcStatus !== 'starter' &&
          planLcStatus !== 'free' &&
          subscription.currentPeriodEnd.getTime() < now.getTime();

        if (hasExpired) {
          // When a paid plan expires, the user falls back to the free Starter plan.
          // The Starter plan never expires, so show "Active" instead of "Expired".
          const effectivePlan = this.getEffectivePlanId(subscription);
          if (effectivePlan === 'starter' || effectivePlan === 'free') {
            return {
              status: 'Active',
              color: 'green',
              message: 'You are on the free Starter plan.',
            };
          }
          return {
            status: 'Expired',
            color: 'red',
            message: `Your subscription expired on ${subscription.currentPeriodEnd.toLocaleDateString()}`,
          };
        }

        return {
          status: 'Active',
          color: 'green',
          message: subscription.cancelAtPeriodEnd
            ? `Active until ${subscription.currentPeriodEnd.toLocaleDateString()}`
            : 'Your subscription is active',
        };
      }
      case 'past_due':
        return {
          status: 'Past Due',
          color: 'yellow',
          message: 'Payment failed. Please update your payment method.',
        };
      case 'cancelled':
        return {
          status: 'Cancelled',
          color: 'red',
          message: 'Your subscription has been cancelled',
        };
      case 'unpaid':
        return {
          status: 'Unpaid',
          color: 'red',
          message: 'Payment required to continue service',
        };
      default:
        return {
          status: 'Inactive',
          color: 'gray',
          message: 'Subscription is inactive',
        };
    }
  }
}

export const subscriptionService = new SubscriptionService();

